export type TranslationResource = Record<string, unknown>

export type TranslationLoader = (
  language: string,
  namespace: string,
) => Promise<TranslationResource | null>

/**
 * GraphQL `Language` enum values that do not match on-disk translation folders.
 * POEditor / repo folders use `zh` and `zh-TW`; the API uses `zh_Hans` / `zh_Hant`.
 */
const TRANSLATION_FOLDER_BY_LANGUAGE: Record<string, string> = {
  'zh-CN': 'zh',
  'zh-Hans': 'zh',
  'zh-Hant': 'zh-TW',
  zh_Hans: 'zh',
  zh_Hant: 'zh-TW',
}

const INTL_LOCALE_BY_LANGUAGE: Record<string, string> = {
  'zh-Hans': 'zh-CN',
  'zh-Hant': 'zh-TW',
  zh_Hans: 'zh-CN',
  zh_Hant: 'zh-TW',
}

export function toStudioTranslationFolder(language: string): string {
  return TRANSLATION_FOLDER_BY_LANGUAGE[language] ?? language
}

export function toStudioIntlLocale(language: string | undefined): string | undefined {
  return language ? (INTL_LOCALE_BY_LANGUAGE[language] ?? language) : undefined
}

export function mergeTranslationResources(
  base: TranslationResource,
  override: TranslationResource,
): TranslationResource {
  const result: TranslationResource = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const baseValue = result[key]

    if (isTranslationResource(baseValue) && isTranslationResource(value)) {
      result[key] = mergeTranslationResources(baseValue, value)
      continue
    }

    result[key] = value
  }

  return result
}

export async function loadMergedTranslationResources({
  language,
  namespace,
  loaders,
}: {
  language: string
  namespace: string
  loaders: TranslationLoader[]
}): Promise<TranslationResource | null> {
  const translationFolder = toStudioTranslationFolder(language)
  const resources = await Promise.all(loaders.map(loader => loader(translationFolder, namespace)))

  return resources.reduce<TranslationResource | null>((mergedResource, resource) => {
    if (!resource) {
      return mergedResource
    }

    if (!mergedResource) {
      return resource
    }

    return mergeTranslationResources(mergedResource, resource)
  }, null)
}

function isTranslationResource(value: unknown): value is TranslationResource {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
